import {
  businessPartnerDetailSchema,
  businessPartnerListSchema,
  costCenterListSchema,
  currencyListSchema,
  paymentMethodListSchema,
  paymentTermListSchema,
  unitConversionListSchema,
  unitListSchema,
  masterDataExportSchema,
  masterDataImportPreviewSchema,
  masterDataImportResultSchema,
  type CreateBusinessPartnerAddressRequest,
  type CreateBusinessPartnerBankAccountRequest,
  type CreateBusinessPartnerContactRequest,
  type CreateBusinessPartnerRequest,
  type CreateBusinessPartnerRoleRequest,
  type CreateBusinessPartnerTaxProfileRequest,
  type CreateBusinessPartnerSettlementRequest,
  type CreateCostCenterRequest,
  type CreateCurrencyRequest,
  type CreatePaymentMethodRequest,
  type CreatePaymentTermRequest,
  type CreateUnitConversionRequest,
  type CreateUnitRequest,
  type PatchBusinessPartnerAddressRequest,
  type PatchBusinessPartnerContactRequest,
  type PatchBusinessPartnerRequest,
  type PatchBusinessPartnerRoleRequest,
  type PatchBusinessPartnerSettlementRequest,
} from '@gb-ticket/shared-types';
import type {
  patchBusinessPartnerBankAccountRequestSchema,
  patchBusinessPartnerTaxProfileRequestSchema,
  patchCostCenterRequestSchema,
  patchCurrencyRequestSchema,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { z } from 'zod';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

const createdSchema = z.object({ id: z.string().uuid() });
const writeResultSchema = z.union([
  z.object({ id: z.string() }).passthrough(),
  z.object({ items: z.array(z.unknown()) }),
]);
type PatchBusinessPartnerBankAccountRequest = z.infer<
  typeof patchBusinessPartnerBankAccountRequestSchema
>;
type PatchBusinessPartnerTaxProfileRequest = z.infer<
  typeof patchBusinessPartnerTaxProfileRequestSchema
>;
type PatchCurrencyRequest = z.infer<typeof patchCurrencyRequestSchema>;
type PatchCostCenterRequest = z.infer<typeof patchCostCenterRequestSchema>;

export const masterDataQueryKey = ['master-data'] as const;

function listKey(resource: string) {
  return [...masterDataQueryKey, resource, { includeInactive: true }] as const;
}

export function useBusinessPartners() {
  return useQuery({
    queryKey: listKey('partners'),
    queryFn: () =>
      apiFetch(
        `/master-data/partners${toSearchParams({ includeInactive: true })}`,
        businessPartnerListSchema,
      ),
  });
}

export function useBusinessPartner(id: string) {
  return useQuery({
    queryKey: [...masterDataQueryKey, 'partners', id],
    enabled: id.length > 0,
    queryFn: () => apiFetch(`/master-data/partners/${id}`, businessPartnerDetailSchema),
  });
}

export function useCreateBusinessPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBusinessPartnerRequest) =>
      apiFetch('/master-data/partners', businessPartnerDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchBusinessPartner(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchBusinessPartnerRequest) =>
      apiFetch(`/master-data/partners/${id}`, businessPartnerDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useAddBusinessPartnerRole(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBusinessPartnerRoleRequest) =>
      apiFetch(`/master-data/partners/${id}/roles`, businessPartnerDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchBusinessPartnerRole(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, body }: { roleId: string; body: PatchBusinessPartnerRoleRequest }) =>
      apiFetch(`/master-data/partners/${id}/roles/${roleId}`, businessPartnerDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useAddBusinessPartnerAddress(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBusinessPartnerAddressRequest) =>
      apiFetch(`/master-data/partners/${id}/addresses`, businessPartnerDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchBusinessPartnerAddress(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      addressId,
      body,
    }: {
      addressId: string;
      body: PatchBusinessPartnerAddressRequest;
    }) =>
      apiFetch(`/master-data/partners/${id}/addresses/${addressId}`, businessPartnerDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useAddBusinessPartnerContact(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBusinessPartnerContactRequest) =>
      apiFetch(`/master-data/partners/${id}/contacts`, businessPartnerDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchBusinessPartnerContact(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      body,
    }: {
      contactId: string;
      body: PatchBusinessPartnerContactRequest;
    }) =>
      apiFetch(`/master-data/partners/${id}/contacts/${contactId}`, businessPartnerDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useAddBusinessPartnerBankAccount(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBusinessPartnerBankAccountRequest) =>
      apiFetch(`/master-data/partners/${id}/bank-accounts`, writeResultSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchBusinessPartnerBankAccount(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bankId,
      body,
    }: {
      bankId: string;
      body: PatchBusinessPartnerBankAccountRequest;
    }) =>
      apiFetch(`/master-data/partners/${id}/bank-accounts/${bankId}`, writeResultSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useAddBusinessPartnerTaxProfile(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBusinessPartnerTaxProfileRequest) =>
      apiFetch(`/master-data/partners/${id}/tax-profiles`, writeResultSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchBusinessPartnerTaxProfile(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taxId, body }: { taxId: string; body: PatchBusinessPartnerTaxProfileRequest }) =>
      apiFetch(`/master-data/partners/${id}/tax-profiles/${taxId}`, writeResultSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useAddBusinessPartnerSettlement(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBusinessPartnerSettlementRequest) =>
      apiFetch(`/master-data/partners/${id}/settlements`, businessPartnerDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchBusinessPartnerSettlement(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      settlementId,
      body,
    }: {
      settlementId: string;
      body: PatchBusinessPartnerSettlementRequest;
    }) =>
      apiFetch(
        `/master-data/partners/${id}/settlements/${settlementId}`,
        businessPartnerDetailSchema,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePaymentTerms() {
  return useQuery({
    queryKey: listKey('payment-terms'),
    queryFn: () =>
      apiFetch(
        `/master-data/payment-terms${toSearchParams({ includeInactive: true })}`,
        paymentTermListSchema,
      ),
  });
}

export function useCreatePaymentTerm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePaymentTermRequest) =>
      apiFetch('/master-data/payment-terms', createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: listKey('payment-methods'),
    queryFn: () =>
      apiFetch(
        `/master-data/payment-methods${toSearchParams({ includeInactive: true })}`,
        paymentMethodListSchema,
      ),
  });
}

export function useCreatePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePaymentMethodRequest) =>
      apiFetch('/master-data/payment-methods', createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useCurrencies() {
  return useQuery({
    queryKey: listKey('currencies'),
    queryFn: () =>
      apiFetch(
        `/master-data/currencies${toSearchParams({ includeInactive: true })}`,
        currencyListSchema,
      ),
  });
}

export function useCreateCurrency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCurrencyRequest) =>
      apiFetch('/master-data/currencies', createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchCurrency(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchCurrencyRequest) =>
      apiFetch(`/master-data/currencies/${id}`, createdSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useUnits() {
  return useQuery({
    queryKey: listKey('units'),
    queryFn: () =>
      apiFetch(`/master-data/units${toSearchParams({ includeInactive: true })}`, unitListSchema),
  });
}

export function useCreateUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUnitRequest) =>
      apiFetch('/master-data/units', createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useUnitConversions() {
  return useQuery({
    queryKey: listKey('unit-conversions'),
    queryFn: () =>
      apiFetch(
        `/master-data/unit-conversions${toSearchParams({ includeInactive: true })}`,
        unitConversionListSchema,
      ),
  });
}

export function useCreateUnitConversion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUnitConversionRequest) =>
      apiFetch('/master-data/unit-conversions', createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useCostCenters() {
  return useQuery({
    queryKey: listKey('cost-centers'),
    queryFn: () =>
      apiFetch(
        `/master-data/cost-centers${toSearchParams({ includeInactive: true })}`,
        costCenterListSchema,
      ),
  });
}

export function useCreateCostCenter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCostCenterRequest) =>
      apiFetch('/master-data/cost-centers', createdSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePatchCostCenter(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchCostCenterRequest) =>
      apiFetch(`/master-data/cost-centers/${id}`, createdSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function usePreviewMasterDataImport() {
  return useMutation({
    mutationFn: (csv: string) =>
      apiFetch('/master-data/import/preview', masterDataImportPreviewSchema, {
        method: 'POST',
        body: JSON.stringify({ csv }),
      }),
  });
}

export function useCommitMasterDataImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (csv: string) =>
      apiFetch('/master-data/import', masterDataImportResultSchema, {
        method: 'POST',
        body: JSON.stringify({ csv }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: masterDataQueryKey });
    },
  });
}

export function useExportMasterData() {
  return useMutation({
    mutationFn: () => apiFetch('/master-data/export', masterDataExportSchema),
  });
}
